import chalk from 'chalk'
import { tagMessage } from './tagMessage'
import { exists } from '../UtilityHelpers/exists'
import { isRunningClientSide } from './isRunningClientSide'

export enum LogType {
    PROPERTY_CHECK = 'property - check',
    FORM_ENTRY_CHECK = 'form entry - check',
    NULL_CHECK = 'null - check',
    SHOULD_NOT_RENDER = 'should not render',
    SHOULD_THROW = 'should throw',
    RESPONSE_MESSAGE = 'response message',
    STATUS_TEXT = 'statusText',
    VALIDATION = 'validation',
    UNEXPECTED = 'unexpected',
    CATCH = 'caught in catch-block',
    ASSERT = 'assertion',
}

export interface LogOptions {
    developmentOnly?: boolean
    caller?: string
    inlineStackTraceDiscardLast?: number
    inlineStackTraceLimit?: number
    logData?: boolean
    obtrusive?: boolean
    modest?: boolean
    error?: boolean
}

const logOptionsDefaults: LogOptions = {
    developmentOnly: true,
}

const isDevelopment = () => process.env.NEXT_PUBLIC_IS_DEVELOPMENT?.toLowerCase() === 'true'

const shouldLogData = (data: unknown) => {
    if (exists(data) && isDevelopment()) {
        return true
    }
    return false
}

/**
 * @description log messages, in a consistent format.
 * By default these messages are only logged in development
 *
 * @example
 * // returns '[readEndpoint]: successful response'
 * log('successful response', 'readEndpoint')
 *
 * @example
 * // logData will log data in development only. Use it in the following way
 * log('successful response', caller, { logData: true }, apiResponse.data)
 *
 * @param message - the main message
 * @param caller - the function that called this log function
 * @param options.developmentOnly - if false, the log will also be rendered in the production env.
 * @param options.obtrusive - if true, the log will be rendered in a hard to mis style.
 * @param options.modest - if true, the log will be rendered in gray so it is less in your face.
 * @param options.error - if true, the log will be rendered in an error style
 */
export const log = (message: string, caller: string, options?: LogOptions, data?: unknown) => {
    const { developmentOnly } = {
        ...logOptionsDefaults,
        ...options,
    }

    if (developmentOnly && !isDevelopment()) return

    if (options?.error) {
        console.log(chalk.bgRed.black(tagMessage(message, caller)), shouldLogData(data) ? data : '')
        return
    }

    if (options?.obtrusive) {
        console.log(chalk.bgMagentaBright.black(tagMessage(message, caller)), shouldLogData(data) ? data : '')
        return
    }

    if (options?.modest) {
        console.log(chalk.gray(tagMessage(message, caller)), shouldLogData(data) ? data : '')
        return
    }

    console.log(chalk.green(tagMessage(message, caller)), shouldLogData(data) ? data : '')
}

/**
 * @description quick way to log data in a hard to mis way
 * Only meant for development / debugging purposes
 * logging wil not be visible in any other environment
 *
 * @example
 * ```typescript
 *
 * scream('this is a string')
 *
 * scream('this is a string', 'myCallingFunction')
 *
 * scream({ foo: 'bar' })
 *
 * scream(42)
 *
 * scream(42, 'myCallingFunction')
 *
 * scream([32, 'foo', 'bar'])
 *
 * ```
 *
 * @param subject
 * @param caller
 * @returns void
 */
export const scream = (subject: number | string | object | undefined | null, caller?: string) => {
    const envIsDevelopment = isDevelopment()
    if (!envIsDevelopment) return
    const subjectIsObject = typeof subject === 'object'
    const subjectIsFalsy = !exists(subject)
    if (!subjectIsObject && !subjectIsFalsy && isNaN(+subject)) {
        log(subject as string, caller ?? 'scream', { obtrusive: true })
        return
    }
    log('======= start of scream =======', caller ?? 'scream', { obtrusive: true })
    console.log(subject)
    log('======== end of scream ========', caller ?? 'scream', { obtrusive: true })
}

export interface ErrorLogOptions extends Omit<LogOptions, 'obtrusive'> {
    logType?: LogType
    asWarning?: boolean
}

const errorLogOptionsDefaults: ErrorLogOptions = {
    asWarning: false,
    developmentOnly: false,
}

/**
 * @description logs error messages, in a consistent format.
 * when called from server: by default these messages are logged in all environments including production
 * when called from client: by default these messages are logged in only in development
 *
 * @example
 * // returns '[myComponent] (property-check):  property is null' in a red color and semantically as an error
 * logerror('property is null', 'myComponent', {messagetype: 'property-check'})
 *
 * @param message - the main message
 * @param caller - the function that called this log function
 * @param options.messagetype - additional info about the log (keep it short).
 * @param options.developmentonly - if false, the log will also be rendered in the production env.
 * @param options.aswarning - if true, the log will be rendered in yellow, and semantically as a warning.
 */
export const logError = (message: string, options?: ErrorLogOptions) => {
    let isClient = isRunningClientSide()

    const {
        asWarning,
        logType: messageType,
        developmentOnly,
    } = {
        ...errorLogOptionsDefaults,
        developmentOnly: !isClient ? errorLogOptionsDefaults.developmentOnly : true,
        ...options,
    }

    if (developmentOnly && !(process.env.NEXT_PUBLIC_IS_DEVELOPMENT?.toLowerCase() === 'true')) return

    const color = asWarning ? chalk.yellow : chalk.red
    const logFunction = asWarning ? console.warn : console.log
    logFunction(
        color(
            tagMessage(
                message,
                options?.caller ??
                    getInlineStackTrace(options?.inlineStackTraceDiscardLast ?? 2, options?.inlineStackTraceLimit) ??
                    undefined,
                messageType,
            ),
        ),
    )
}

/** generates a consise stacktrace for use in caller argument */
export const getInlineStackTrace = (discard: number = 2, limit: number = 2): string | null => {
    const error = new Error()
    const stack: string[] | undefined = error.stack?.split('\n')

    for (let i = 0; i <= discard; i++) {
        stack?.shift()
    }

    const functionNames: string | null =
        stack?.reduce((accumulator: string, current: string, index: number) => {
            const cleanedUpAccumulator = accumulator
                .trim()
                .replace(/^at\s/g, '')
                .replace(/\(.*\)/g, '')
                .trim()
            const part = current
                .trim()
                .replace(/\(.*\)/g, '')
                .replace(/^at\s/g, '')
                .trim()
            if (index >= limit) return accumulator
            return `${part} > ${cleanedUpAccumulator}`
        }) ?? null
    return functionNames
}

/**
 * @description logs error messages using the logError function, only when assertion is false
 * @param assertion - boolean that is the result of an assertion
 * @param message - the main message
 * @param caller - the function that called this assertion
 */
export const assert = (assertion: boolean, message: string, options?: ErrorLogOptions): void => {
    if (assertion) return
    const caller =
        options?.caller ??
        getInlineStackTrace(options?.inlineStackTraceDiscardLast, options?.inlineStackTraceLimit) ??
        undefined
    logError(message, { logType: LogType.ASSERT, caller })
}
